import { Button, Input, NativeSelect, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import React, { useEffect, useMemo, useState } from 'react'
import type { NavigateFunction } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, Modal } from '@mangovoice/mango-ui'
import { ExtensionInstance, PaginatedApiResult } from '@mangovoice/types'

import type { SwitchboardApi } from '../api/switchboardApi'
import { extensionsListAtom } from '../atoms'
import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'
import { convertReactFlowToLegacyFormat } from '../converters/toLegacy/index'
import { useCreateSwitchboard } from '../queries/hooks'
import type { SwitchboardTranslateFn } from '../types'
import { text } from '../utils/text'

export interface SwitchboardCreatePageProps {
  /** Switchboard API instance */
  api: SwitchboardApi
  /** Navigation function (from react-router useNavigate) */
  navigate: NavigateFunction
  /** Route path for the switchboards list page */
  listRoute: string
  /** Route template for edit page (e.g., "/switchboards/:extensionDialPlan") */
  editRouteTemplate: string
  /** PBX ID that this switchboard belongs to */
  pbxId?: number
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Optional Field component (defaults to Chakra UI Field) */
  FieldComponent?: typeof Field
  /** Whether the dialog is open (for modal usage) */
  open?: boolean
  /** Handler for when the dialog should close (for modal usage) */
  onOpenChange?: (details: { open: boolean }) => void
  /** Optional function to create ExtensionDialPlan extension. If not provided, extension must exist first. */
  onCreateExtension?: (params: {
    pbx_id: number
    name: string
    number: number | string
    omit_from_directory?: 0 | 1 | boolean
  }) => Promise<{ ok: boolean; data?: ExtensionInstance; status?: number }>
  /** Optional function to fetch extensions via API. If provided, will fetch ExtensionDialPlan extensions to calculate next available number. */
  fetchExtensions?: (params: {
    pbx_id: number
    type: 'ExtensionDialPlan'
    limit?: number
  }) => Promise<{
    ok: boolean
    data?: PaginatedApiResult<ExtensionInstance>
    status?: number
  }>
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
  // If fetchExtensions is provided, it will be used to fetch ExtensionDialPlan extensions
}

export const SwitchboardCreatePage: React.FC<SwitchboardCreatePageProps> = ({
  api,
  navigate,
  listRoute,
  editRouteTemplate,
  pbxId,
  toast,
  FieldComponent = Field,
  open = true,
  onOpenChange,
  onCreateExtension: _onCreateExtension, // No longer used - backend auto-creates extension
  fetchExtensions,
  t,
}) => {
  const [name, setName] = useState<string>('')
  const [number, setNumber] = useState<string>('')
  const [omitFromDirectory, setOmitFromDirectory] = useState<string>('No')
  const createMutation = useCreateSwitchboard({ api })

  // Read extensions list from Jotai atom (fallback for calculating next available number)
  const extensionsListFromAtom = useAtomValue(extensionsListAtom)

  // Fetch ExtensionDialPlan extensions via API if fetchExtensions prop is provided
  // Use query key that matches the list page so it uses cached data that's already fetched
  const { data: fetchedExtensionsData } = useQuery<
    PaginatedApiResult<ExtensionInstance>
  >({
    queryKey: ['extensions', pbxId, 'ExtensionDialPlan', 'all'],
    queryFn: async () => {
      if (!pbxId || !fetchExtensions) {
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        }
      }

      const response = await fetchExtensions({
        pbx_id: pbxId,
        type: 'ExtensionDialPlan',
        limit: 10000, // Large limit to get all switchboard extensions
      })

      if (!response.ok || !response.data) {
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        }
      }

      return response.data
    },
    enabled: !!pbxId && !!fetchExtensions, // Always enabled so it uses cached data from list page
    retry: false,
  })

  // Use fetched extensions if available, otherwise fall back to atom
  // Filter to only ExtensionDialPlan extensions
  const extensionsList = useMemo(() => {
    const list = fetchedExtensionsData?.results || extensionsListFromAtom || []
    // Filter to only ExtensionDialPlan extensions (switchboards)
    return list.filter((ext) => ext.type === 'ExtensionDialPlan')
  }, [fetchedExtensionsData, extensionsListFromAtom])

  // Calculate next available extension number (matching legacy logic)
  // Legacy: MAX(extensions.number) + 1, skip certain numbers (211, 311, etc.), min 100, max 99999
  // Only considers ExtensionDialPlan extensions (switchboards)
  const nextAvailableNumber = useMemo(() => {
    if (!extensionsList || extensionsList.length === 0) {
      return 1000 // Default starting number
    }

    // Get all extension numbers and find the max
    // ExtensionInstance.number can be string or number, convert to number
    const extensionNumbers = extensionsList
      .map((ext) => {
        const num =
          typeof ext.number === 'string' ? parseInt(ext.number, 10) : ext.number
        return isNaN(num) ? null : num
      })
      .filter((num): num is number => num !== null)

    if (extensionNumbers.length === 0) {
      return 1000
    }

    const maxNumber = Math.max(...extensionNumbers)
    let nextNumber = maxNumber + 1

    // Skip certain numbers (211, 311, 411, 511, 611, 711, 811, 911, 933) - matching legacy
    const skipNumbers = [211, 311, 411, 511, 611, 711, 811, 911, 933]
    while (skipNumbers.includes(nextNumber)) {
      nextNumber += 1
    }

    // Ensure minimum is 100 (or 0)
    if (nextNumber < 100 && nextNumber !== 0) {
      nextNumber = 100
    }

    // Ensure maximum is 99999
    if (nextNumber > 99999) {
      nextNumber = 99999
    }

    return nextNumber
  }, [extensionsList])

  // Prefill should use the computed next available number directly:
  // first switchboard = 1000, then increment by one per creation.
  const defaultNumber = nextAvailableNumber
  const defaultName = `Switchboard - ${nextAvailableNumber}`

  // Pre-fill form when dialog opens or when extensions list changes
  useEffect(() => {
    if (open && defaultNumber) {
      // Pre-fill number (only if empty)
      setNumber((current) => {
        if (!current) {
          return String(defaultNumber)
        }
        return current
      })

      // Pre-fill name with format "Switchboard - {number}"
      setName((current) => {
        if (!current) {
          return defaultName
        }
        return current
      })
    }
  }, [open, defaultNumber, defaultName]) // Only depend on open and calculated defaults

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      // Clear form when dialog closes so it pre-fills next time
      setName('')
      setNumber('')
      setOmitFromDirectory('No')
      if (onOpenChange) {
        onOpenChange(details)
      } else {
        navigate(listRoute)
      }
    } else if (onOpenChange) {
      onOpenChange(details)
    }
  }

  const handleClose = () => {
    // Clear form when dialog closes
    setName('')
    setNumber('')
    setOmitFromDirectory('No')
    if (onOpenChange) {
      onOpenChange({ open: false })
    } else {
      navigate(listRoute)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate name
    if (!name.trim()) {
      toast?.create({
        title: text(t, 'switchboard.create.nameRequired', 'Name Required'),
        description: text(
          t,
          'switchboard.create.nameRequiredDescription',
          'Please enter a name for this switchboard.',
        ),
        type: 'error',
      })
      return
    }

    // Validate number - must be 0 or 100-99999 (matching legacy validation)
    const extensionDialPlanNumber = parseInt(number, 10)
    if (isNaN(extensionDialPlanNumber)) {
      toast?.create({
        title: text(t, 'switchboard.create.invalidNumber', 'Invalid Number'),
        description: text(
          t,
          'switchboard.create.invalidNumberDescription',
          'Please enter a valid number.',
        ),
        type: 'error',
      })
      return
    }

    // Number must be 0 or between 100-99999
    if (
      extensionDialPlanNumber !== 0 &&
      (extensionDialPlanNumber < 100 || extensionDialPlanNumber > 99999)
    ) {
      toast?.create({
        title: text(t, 'switchboard.create.invalidNumber', 'Invalid Number'),
        description: text(
          t,
          'switchboard.create.invalidNumberRange',
          'Number must be 0 or between 100 and 99999.',
        ),
        type: 'error',
      })
      return
    }

    if (!pbxId) {
      toast?.create({
        title: text(t, 'switchboard.create.pbxIdRequired', 'PBX ID Required'),
        description: text(
          t,
          'switchboard.create.pbxIdRequiredDescription',
          'PBX ID is required to create a switchboard.',
        ),
        type: 'error',
      })
      return
    }

    try {
      // Create empty flow data (matching legacy default structure)
      const emptyFlowData: SwitchboardFlowData = {
        nodes: [],
        edges: [],
      }

      // Convert to legacy format for API compatibility (same pattern as edit page)
      // Backend requires json_data to be a list (array)
      // Pass messaging groups to convert SMS sendFrom IDs to phone numbers (empty array is fine for new switchboards)
      const oldFormatData = convertReactFlowToLegacyFormat(emptyFlowData, [])
      const oldFormatArray = [oldFormatData] // Wrap in array - API validation requires list

      // Backend auto-creates ExtensionDialPlan extension, then creates switchboard
      // New API format: name, number, pbx_id, omit_from_directory, json_data
      const response = await createMutation.mutateAsync({
        name: name.trim(),
        number: extensionDialPlanNumber,
        pbx_id: pbxId,
        omit_from_directory: omitFromDirectory === 'Yes',
        json_data: oldFormatArray, // Must be a list/array
      })

      toast?.create({
        title: text(
          t,
          'switchboard.create.createdSuccess',
          'Switchboard created',
        ),
        type: 'success',
      })

      // Navigate to the edit page using extension_dial_plan from response
      const extensionDialPlanId = response.extension_dial_plan
      const editRoute = editRouteTemplate.replace(
        ':extensionDialPlan',
        String(extensionDialPlanId),
      )
      navigate(editRoute)
    } catch (error: unknown) {
      // Parse API error response structure
      let errorTitle = text(
        t,
        'switchboard.create.createFailed',
        'Failed to create switchboard',
      )
      let errorMessage = text(
        t,
        'switchboard.create.genericError',
        'An error occurred',
      )

      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error.data as {
          type?: string
          message?: string
          code?: string
          parameters?: Array<{
            name: string
            code: string
            message: string
          }>
        }

        // Check for duplicate extension number error (matching legacy error format)
        // Legacy: "Cannot add Switchboard 'Switchboard - 7070': An extension with the number '7070' already exists."
        const errorMessageStr = apiError.message || ''
        const isDuplicateError =
          errorMessageStr.includes('already exists') ||
          errorMessageStr.includes('does_not_exist') ||
          apiError.parameters?.some(
            (p) =>
              p.code === 'does_not_exist' ||
              p.code === 'duplicate' ||
              p.name === 'extension_dial_plan',
          )

        if (isDuplicateError) {
          // Format duplicate extension error (matching legacy format)
          errorTitle = text(
            t,
            'switchboard.create.cannotAddSwitchboard',
            "Cannot add Switchboard '{{name}}'",
            { name },
          )
          errorMessage = text(
            t,
            'switchboard.create.extensionAlreadyExists',
            "An extension with the number '{{number}}' already exists.",
            { number: String(extensionDialPlanNumber) },
          )
        } else if (apiError.parameters && apiError.parameters.length > 0) {
          // Format validation errors from API
          const paramErrors = apiError.parameters
            .map((p) => `${p.name}: ${p.message}`)
            .join(', ')
          errorMessage = paramErrors
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      toast?.create({
        title: errorTitle,
        description: errorMessage,
        type: 'error',
      })
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title={text(t, 'switchboard.create.dialogTitle', 'New Switchboard')}
      maxWidth="500px"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            colorPalette="neutral"
            onClick={handleClose}
            px={5}
            py={2.5}
            borderRadius="lg"
            borderColor="border.default"
            _hover={{
              borderColor: 'border.emphasized',
              bg: 'bg.subtle',
            }}
          >
            {text(t, 'switchboard.create.close', 'Close')}
          </Button>
          <Button
            type="submit"
            form="create-switchboard-form"
            colorPalette="brand"
            loading={createMutation.isPending}
            disabled={!name.trim() || !number.trim()}
            px={5}
            py={2.5}
            borderRadius="lg"
          >
            {text(
              t,
              'switchboard.create.submitButton',
              'Create New Switchboard',
            )}
          </Button>
        </>
      }
    >
      <form id="create-switchboard-form" onSubmit={handleSubmit}>
        <VStack gap={4} align="stretch">
          <FieldComponent.Root required>
            <FieldComponent.Label>
              {text(t, 'switchboard.list.edit.nameLabel', 'Name')}
            </FieldComponent.Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={text(
                t,
                'switchboard.list.edit.namePlaceholder',
                'e.g., Switchboard - 7074',
              )}
              borderColor="border.default"
              pl={3}
              _focusVisible={{
                borderColor: 'border.emphasized',
                boxShadow: '0 0 0 1px var(--chakra-colors-border-emphasized)',
              }}
            />
          </FieldComponent.Root>

          <FieldComponent.Root required>
            <FieldComponent.Label>
              {text(t, 'switchboard.list.edit.numberLabel', 'Number')}
            </FieldComponent.Label>
            <Input
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={text(
                t,
                'switchboard.list.edit.numberPlaceholder',
                'e.g., 7074',
              )}
              min={0}
              max={99999}
              borderColor="border.default"
              pl={3}
              _focusVisible={{
                borderColor: 'border.emphasized',
                boxShadow: '0 0 0 1px var(--chakra-colors-border-emphasized)',
              }}
            />
          </FieldComponent.Root>

          <FieldComponent.Root>
            <FieldComponent.Label>
              {text(
                t,
                'switchboard.list.edit.omitFromDirectoryLabel',
                'Omit From Directory',
              )}
            </FieldComponent.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={omitFromDirectory}
                onChange={(e) => setOmitFromDirectory(e.target.value)}
                borderColor="border.default"
                pl={3}
                _focusVisible={{
                  borderColor: 'border.emphasized',
                  boxShadow: '0 0 0 1px var(--chakra-colors-border-emphasized)',
                }}
              >
                <option value="No">
                  {text(t, 'switchboard.list.edit.omitNo', 'No')}
                </option>
                <option value="Yes">
                  {text(t, 'switchboard.list.edit.omitYes', 'Yes')}
                </option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </FieldComponent.Root>
        </VStack>
      </form>
    </Modal>
  )
}
